import React from 'react';
import {
  Checkbox,
  InternalStandardProps,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
} from '@mui/material';
import { TableHeadProps } from '@mui/material/TableHead';
import { Resource } from '../../../../crud-graphql-containers/Utils/genericTypes';
import { isReactComponent } from '../../../../crud-core/utils/TypescriptUtils';
import { OrderDirection } from '../../../../crud-core/utils/Sort';

export type TableListHeadProps = {
  bulkSelectionPossible?: boolean;
  children?: React.ReactNode;
  items: Object[];
  labelSortTooltip?: string;
  selected?: string[];
  onRequestSort?: (event: React.MouseEvent<HTMLElement>, field: string) => void;
  onSelectAllClick?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  order?: OrderDirection;
  orderBy?: string;
  resourceConfig?: Resource;
  tableHeadProps?: TableHeadProps;
} & InternalStandardProps<React.HTMLAttributes<HTMLElement>>;

const TableListHead: React.FC<TableListHeadProps> = ({
  bulkSelectionPossible,
  children,
  className,
  items,
  labelSortTooltip = 'Sort',
  onRequestSort = () => {
    return;
  },
  onSelectAllClick = () => {
    return;
  },
  order,
  orderBy,
  selected = [],
  style,
  tableHeadProps,
}: TableListHeadProps) => {
  const createSortHandler = (property: string) => (event: React.MouseEvent<HTMLElement>) => {
    return onRequestSort(event, property);
  };

  return (
    <TableHead {...tableHeadProps} className={className} style={style}>
      <TableRow>
        {bulkSelectionPossible && (
          <TableCell padding="checkbox">
            <Checkbox
              indeterminate={selected.length > 0 && selected.length < items.length}
              checked={selected.length === items.length}
              onChange={onSelectAllClick}
            />
          </TableCell>
        )}

        {React.Children.map(children, (field) => {
          if (!field || !isReactComponent(field)) {
            return null;
          }

          const label = field.props.label ? field.props.label : field.props.field;

          return (
            <TableCell
              key={field.props.field}
              sortDirection={orderBy === field.props.field ? order : false}
              {...field.props.tableCellProps}
            >
              {orderBy ? (
                <Tooltip
                  title={labelSortTooltip}
                  placement={field.props.numeric ? 'bottom-end' : 'bottom-start'}
                  enterDelay={300}
                >
                  <TableSortLabel
                    active={orderBy === field.props.field}
                    direction={order}
                    onClick={createSortHandler(field.props.field)}
                  >
                    {label}
                  </TableSortLabel>
                </Tooltip>
              ) : (
                <span>{label}</span>
              )}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
};

// eslint-disable-next-line import/no-default-export
export default TableListHead;
